import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import QuoteHeader from "../../components/Quote/QuoteHeader";
import QuoteItemsTable from "../../components/Quote/QuoteTable";
import ServiceDetails from "../../components/Quote/QuoteServiceDetail";
import QuoteActions from "../../components/Quote/QuoteActions";
import PackageModal from "../../components/Modal/Quote/ServiceModal";
import FileModalLocation from "../../components/Modal/FileModalLocation";
import api from "../../../utils/axios";
import { getSpRunNumber } from "../../../utils/SamuiUtils";
import Swal from "sweetalert2";
import QuoteDocumentInfo from "../../components/Quote/QuoteDocumentInfo";


export default function QuotePage() {
  const { AR_Id, Doc_Id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("AR_Id");
  const doc_no = searchParams.get("Doc_No");
  const mode = searchParams.get("mode");
  const isEdit = location.pathname.includes("/quote/edit");
  const backLink = location.state?.backLink || null;
  const backLabel = location.state?.backLabel || "หน้าก่อนหน้า";
  const fromJobFile = location.state?.fromJobFile === true;
  const jobFileBackLink = location.state?.jobFileBackLink || "/jobfile";
  const jobFileContractLabel = location.state?.jobFileContractLabel || null;
  const jobFileContractLink = location.state?.jobFileContractLink || null;

  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showFileModal, setShowFileModal] = useState(false);

  const [serviceLocations, setServiceLocations] = useState([]);  
  const [contractLocation, setContractLocation] = useState(null);

  const [contractInfo, setContractInfo] = useState({ hasContract: false });
  const [subLocations, setSubLocations] = useState([]);

  const guessFileType = (fileName) => {
    const ext = (fileName.split(".").pop() || "").toLowerCase();
    const map = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    };
    return map[ext] || "application/octet-stream";
  };

  const [header, setHeader] = useState({
    Comp_Id: localStorage.getItem("company"),
    Doc_No: "",
    Doc_Date: new Date().toISOString().split("T")[0],
    Doc_Status: 1,
    Doc_Code: 6,
    Doc_Detail: "",
    Doc_Remark: "",
    AR_Id: AR_Id || id,
    AR_Name: "",
    ServiceAddr_Id: null,
    ServiceAddr_Name: "",
    Contract_ServiceAddr_Id: null,
    Contract_ServiceAddr_Name: "",
    Contract_ServiceAddr_FullText: "",
    Contract_ContactName: "",
    Contract_ContactTel: "",
    DisplayName: "",
    IsVat: 0,
    IsVatIn: 0,
    Sale_Emp_Id: null,
  });

  const [serviceDetail, setServiceDetail] = useState({
    Contract_Period: "",
    Contract_Unit: "",
    Warranty: "",
    Warranty_Unit: "",
    Service_Time: "",
    Service_Unit: "",
    Payment_Condition: "",
  });

  const editDocNo = header.Doc_No || doc_no || "";
  const editTitle = `แก้ไขใบเสนอราคา${editDocNo ? ` ${editDocNo}` : ""}`;
  const contractListLink = location.state?.contractListLink || `/contractlist/${header.AR_Id || id}`;
  const contractListLabel = location.state?.contractListLabel || "สัญญา";
  const fromPage = location.state?.from || null;
  const fromJobId = location.state?.jobId || null;
  const topbarBreadcrumbs = fromJobFile && isEdit
    ? [
        { label: "งานช่าง", link: "/job-management" },
        { label: "แฟ้มงาน", link: jobFileBackLink },
        ...(jobFileContractLabel
          ? [{ label: jobFileContractLabel, link: jobFileContractLink || jobFileBackLink }]
          : []),
        { label: editTitle },
      ]
    : fromPage && isEdit
    ? [
        { label: "ปฏิทินงานช่าง", link: "/job/calendar" },
        ...(fromJobId ? [{ label: `งาน ${fromJobId}`, link: fromPage.path }] : []),
        { label: fromPage.label, link: fromPage.path },
        { label: editTitle },
      ]
    : isEdit && backLink
    ? [
        { label: "ลูกค้า", link: "/sale" },
        { label: "รายการลูกค้า", link: "/sale" },
        { label: contractListLabel, link: contractListLink },
        { label: backLabel, link: backLink },
        { label: editTitle },
      ]
    : isEdit
    ? [
        { label: "ลูกค้า", link: "/sale" },
        { label: "รายการลูกค้า", link: "/sale" },
        { label: "ใบเสนอราคา", link: `/quotelist/${header.AR_Id || id}` },
        { label: editTitle },
      ]
    : [
        ...(backLink ? [{ label: backLabel, link: backLink }] : [{ label: "ลูกค้า", link: "/sale" }]),
        { label: "ใบเสนอราคา", link: `/quotelist/${header.AR_Id || id}` },
        { label: "สร้างใบเสนอราคาใหม่" },
      ];

  const formatPhone = (tel) => {
    if (!tel) return "";
    const digits = tel.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };


  useEffect(() => {
    if (Doc_Id || !AR_Id) return;

    const fetchCustomer = async () => {
      try {
        const runNo = await getSpRunNumber("QT");
        if (runNo) setHeader((prev) => ({ ...prev, Doc_No: runNo }));

        const res = await api.get(`/get-tb-set-ar1/${AR_Id}`);
        if (res.data.status === "OK" && res.data.result?.recordset?.length > 0) {
          const c = res.data.result.recordset[0];

          const fullName = `${c.AR_Name || ""} ${c.AR_LastName || ""}`.trim();

          const formatTaxId = (tax) => {
            if (!tax) return "";
            let digits = tax.toString().replace(/\D/g, "").slice(0, 13);
            if (digits.length >= 13)
              return `${digits[0]}-${digits.slice(1, 5)}-${digits.slice(5, 10)}-${digits.slice(10, 12)}-${digits[12]}`;
            return tax;
          };

          const formatPhone = (tel) => {
            if (!tel) return "";
            const digits = tel.replace(/\D/g, "").slice(0, 10);
            if (digits.length <= 3) return digits;
            if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
            return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
          };

          setHeader((prev) => ({
            ...prev,
            AR_Id: c.AR_Id,
            AR_Name: fullName,
            AR_ContactName: prev.AR_ContactName || c.AR_ContactName,
            DisplayName: prev.DisplayName || c.DisplayName?.trim() || "",
            AR_TaxNo: formatTaxId(c.AR_TaxNo || ""),
            AR_Tel: formatPhone(c.AR_Tel || ""),
          }));
        }
      } catch {}
    };

    fetchCustomer();
  }, [AR_Id, Doc_Id]);

  useEffect(() => {
  if (!header.AR_Id) return;

  api
    .get(`/get-tb-set-service-addr/${header.AR_Id}`)
    .then((res) => {
      if (res.data.status !== "OK") return;

      const list = res.data.result?.recordset || [];

      // ========================
      // 🔥 แยก Purpose ด้วยชื่อ (รองรับ ID ที่ไม่ fixed)
      // ========================
      const isServiceAddr = (x) =>
        (x.ServicePurpose_Name || "").trim() === "ที่อยู่บริการ" ||
        Number(x.ServicePurpose) === 6;
      const isContractAddr = (x) =>
        (x.ServicePurpose_Name || "").trim() === "ที่อยู่ทำสัญญา" ||
        Number(x.ServicePurpose) === 7;

      const purpose3List = list.filter(isContractAddr);
      const purpose2List = list.filter(isServiceAddr);

      // ========================
      // 🔥 เลือกสถานที่สัญญา (Purpose 3)
      // ========================
      const contractLoc =
        purpose3List.find((x) => x.IsDefault === 1) ||
        purpose3List[0] ||
        null;

      // ========================
      // 🔥 เลือกสถานที่บริการ (Purpose 2) — ถ้ามีหลายที่ให้เลือก default ก่อน แล้ว fallback [0]
      // ========================
      const serviceLoc =
        purpose2List.find((x) => x.IsDefault === 1) ||
        purpose2List[0] ||
        null;

      // ========================
      // 🔥 อัปเดต State
      // ========================
      setContractLocation(contractLoc);
      setServiceLocations(purpose2List);

      // ========================
      // 🔥 ใส่ค่า Purpose 3 -> header
      // ========================
      if (contractLoc) {
        const full3 = [
          contractLoc.AddrNo || "",
          contractLoc.SubDistrictName
            ? `ต.${contractLoc.SubDistrictName}`
            : "",
          contractLoc.DistrictName
            ? `อ.${contractLoc.DistrictName}`
            : "",
          contractLoc.ProvinceName
            ? `จ.${contractLoc.ProvinceName}`
            : "",
          contractLoc.Zipcode || "",
        ]
          .filter(Boolean)
          .join(" ");

        setHeader((prev) => ({
          ...prev,
          Contract_ServiceAddr_Id: contractLoc.ServiceAddr_Id,
          Contract_ServiceAddr_Name: contractLoc.ServiceAddr_Name,
          Contract_ServiceAddr_FullText: full3,
          Contract_ContactName: prev.Contract_ContactName || contractLoc.AR_ContactName || "",
          Contract_ContactTel: prev.Contract_ContactTel || formatPhone(contractLoc.AR_Tel || ""),

        }));
      }

      // ========================
      // 🔥 ใส่ค่า Purpose 2 -> header (ถ้ามี exact 1 record)
      // ========================
      if (
        serviceLoc &&
        (!header.ServiceAddr_Id || !header.ServiceAddr_FullText)
      ) {
        const full2 = [
          serviceLoc.AddrNo || "",
          serviceLoc.SubDistrictName
            ? `ต.${serviceLoc.SubDistrictName}`
            : "",
          serviceLoc.DistrictName ? `อ.${serviceLoc.DistrictName}` : "",
          serviceLoc.ProvinceName ? `จ.${serviceLoc.ProvinceName}` : "",
          serviceLoc.Zipcode || "",
        ]
          .filter(Boolean)
          .join(" ");

        setHeader((prev) => ({
          ...prev,
          ServiceAddr_Id: serviceLoc.ServiceAddr_Id,
          ServiceAddr_Name: serviceLoc.ServiceAddr_Name,
          ServiceAddr_FullText: full2,
          Service_ContactName: prev.Service_ContactName || serviceLoc.AR_ContactName || "",
          Service_ContactTel: prev.Service_ContactTel || formatPhone(serviceLoc.AR_Tel || ""),
        }));
      }
    })
    .catch((err) => {
      console.error("❌ โหลด service address fail:", err);
    });
}, [header.AR_Id]);


  useEffect(() => {
    if (!Doc_Id) return;

    const fetchQuote = async () => {
      try {
        const res = await api.get(`/get-quote/${Doc_Id}`);

        const h = res.data.header;
        const d = res.data.details || [];
        const f = res.data.files || [];
        const contractInfoData = res.data.contractInfo || { hasContract: false };

        setContractInfo(contractInfoData);

        if (contractInfoData.hasContract && Number(contractInfoData.contractStatus) !== 5) {
          setIsLocked(true);
        } else {
          setIsLocked(false);
        }

        const formatPhone = (val) => {
          if (!val) return "";
          const digits = val.replace(/\D/g, "").slice(0, 10);
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        };

        const formatTaxId = (tax) => {
          if (!tax) return "";
          let digits = tax.toString().replace(/\D/g, "").slice(0, 13);
          if (digits.length >= 13)
            return `${digits[0]}-${digits.slice(1, 5)}-${digits.slice(5, 10)}-${digits.slice(10, 12)}-${digits[12]}`;
          if (digits.length >= 11)
            return `${digits[0]}-${digits.slice(1, 5)}-${digits.slice(5, 10)}-${digits.slice(10)}`;
          if (digits.length >= 6)
            return `${digits[0]}-${digits.slice(1, 5)}-${digits.slice(5)}`;
          if (digits.length >= 2)
            return `${digits[0]}-${digits.slice(1)}`;
          return digits;
        };

        setHeader((prev) => ({
          ...prev,
          ...h,
          Doc_Date: h.Doc_Date ? h.Doc_Date.split("T")[0] : "",
          AR_Id: h.AR_Id,
          Doc_Ref: h.Doc_No,
          AR_ContactName: h.AR_ContactName,
          AR_Tel: formatPhone(h.AR_Tel || ""),
          AR_TaxNo: formatTaxId(h.AR_TaxNo || ""),
          DisplayName: h.DisplayName || "",
          ServiceAddr_Name: h.ServiceAddr_Name || "",
          ServiceAddr_FullText: h.ServiceAddr_FullText,
          IsVat: h.IsVat ?? 0,
          IsVatIn: h.IsVatIn ?? 0,
        }));

        setServiceDetail({
          Contract_Period: h.Contract_Period || "",
          Contract_Unit: h.Contract_Unit || "",
          Warranty: h.Warranty || "",
          Warranty_Unit: h.Warranty_Unit || "",
          Service_Time: h.Service_Time || "",
          Service_Unit: h.Service_Unit || "",
          Payment_Condition: h.Payment_Condition || "",
        });

        setItems(
          d.map((item) => {
            let previewUrl = null;

            try {
              if (item.Item_Img?.data) {
                const blob = new Blob([Uint8Array.from(item.Item_Img.data)], {
                  type: "image/jpeg",
                });
                previewUrl = URL.createObjectURL(blob);
              }
            } catch (err) {
              console.error("❌ Error creating blob:", err);
            }

            return {
              Pack_Id: item.Item_Id,
              Pack_Name: item.Item_Name,
              Item_Qty: Number(item.Item_Qty) || 1,
              Item_Price_Unit: Number(item.Item_Price_Unit) || 0,
              Item_Unit: item.Item_Unit || "สินค้า",
              Item_Discount: Number(item.Item_Discount) || 0,
              Item_Img: item.Item_Img || null,
              Item_Img_Url: previewUrl,
            };
          })
        );

        setSelectedFiles(
          f.map((file) => ({
            File_Id: file.F_Id,
            File_Name: file.File_Name,
            File_Url: file.File_Url,
            size: file.File_Size || 1024,
            type: guessFileType(file.File_Name),
            isNew: false,
            rawFile: null,
          }))
        );

        const subs = res.data.subLocations || [];
        setSubLocations(
          subs.map((s) => ({
            Sub_Id: s.Sub_Id,
            Sub_Name: s.Sub_Name || "",
            Qty: Number(s.Qty) || 0,
            Price_Unit: Number(s.Price_Unit) || 0,
            Discount: Number(s.Discount) || 0,
            IsVat: !!s.IsVat,
            Sub_Img: s.Sub_Img || null,
            Sub_Img_Url: s.Sub_Img_Url || null,
          }))
        );

        setIsDataLoaded(true);
      } catch (err) {
        console.error("โหลดข้อมูลใบเสนอราคาไม่สำเร็จ:", err);
        Swal.fire("ผิดพลาด!", "โหลดข้อมูลใบเสนอราคาไม่สำเร็จ", "error");
        setIsDataLoaded(true); // แม้จะ error ก็ให้ตั้งเป็น true เพื่อไม่ให้รอ
      }
    };

    fetchQuote();
  }, [Doc_Id]);

  // สำหรับหน้าใหม่ (ไม่มี Doc_Id) ให้ตั้ง isDataLoaded เป็น true ทันที
  useEffect(() => {
    if (!Doc_Id) {
      setIsDataLoaded(true);
    }
  }, [Doc_Id]);

  useEffect(() => {
    if (!Doc_Id || !header.ServiceAddr_Id) return;

    const reloadAddress = async () => {
      try {
        const res = await api.get(`/get-tb-set-service-addr/${header.AR_Id}`);
        if (res.data.status === "OK" && Array.isArray(res.data.result?.recordset)) {
          const found = res.data.result.recordset.find(
            (x) => x.ServiceAddr_Id === header.ServiceAddr_Id
          );

          if (found) {
            const formatAddress = (loc) =>
              [
                loc.AddrNo || "",
                loc.SubDistrictName ? `ต.${loc.SubDistrictName}` : "",
                loc.DistrictName ? `อ.${loc.DistrictName}` : "",
                loc.ProvinceName ? `จ.${loc.ProvinceName}` : "",
                loc.Zipcode || "",
              ]
                .filter(Boolean)
                .join(" ");

            setHeader((prev) => ({
              ...prev,
              ServiceAddr_FullText: formatAddress(found),
            }));
          }
        }
      } catch (err) {
        console.error("โหลดที่อยู่ล้มเหลว:", err);
      }
    };

    reloadAddress();
  }, [Doc_Id, header.ServiceAddr_Id]);
  const handleSave = async () => {
    try {

      if (contractInfo.hasContract && Number(contractInfo.contractStatus) !== 5) {
        Swal.fire(
          "ไม่สามารถบันทึกได้",
          "ใบเสนอราคานี้มีการทำสัญญาแล้ว ไม่สามารถแก้ไขได้อีก",
          "warning"
        );
        return;
      }

      if (isLocked) {
        Swal.fire("เอกสารถูกอนุมัติแล้ว", "ไม่สามารถแก้ไขได้", "info");
        return;
      }

      // ================================
      // 🟡 ถ้าเอกสารถูกอนุมัติแล้ว (2) และอยู่ในโหมดแก้ไข
      // ================================
      if (Doc_Id && Number(header.Doc_Status) === 2) {
        const result = await Swal.fire({
          title: "ยืนยันการแก้ไขเอกสาร?",
          html: `
            <div class="text-center leading-relaxed">

              <!-- บรรทัด 1 -->
              ใบเสนอราคา 
              <strong class="text-green-600">${header.Doc_No}</strong> 
              ถูกอนุมัติแล้ว
              <br><br>

              <!-- บรรทัด 2 -->
              หากมีการแก้ไขข้อมูล
              <br>

              <!-- บรรทัด 3 -->
              <span class="text-red-600 font-semibold">
                สถานะจะถูกรีเซ็ทกลับเป็น “รออนุมัติ” ใหม่อีกครั้ง
              </span>

              <br><br>

              <!-- บรรทัด 4: ต้องการดำเนินการต่อหรือไม่? -->
              ต้องการดำเนินการต่อหรือไม่?

            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "ยืนยันแก้ไข",
          cancelButtonText: "ยกเลิก",
          confirmButtonColor: "#E13F57",
        });


        if (!result.isConfirmed) return;

        // 🟢 ผู้ใช้ยืนยัน → รีเซ็ตให้เป็นรออนุมัติ
        setHeader(prev => ({ ...prev, Doc_Status: 1 }));
      }

      // ================================
      // 🔍 ตรวจข้อมูลจำเป็น
      // ================================
      const missing = [];
      if (!header.Doc_Date) missing.push("วันที่เอกสาร");
      if (!header.AR_Name) missing.push("ชื่อลูกค้า");
      if (!header.ServiceAddr_Id) missing.push("สถานที่บริการ");
      if (!header.Sale_Emp_Id) missing.push("พนักงานขาย");
      if (!header.DisplayName) missing.push("ชื่อที่ใช้ในสัญญา");

      if (missing.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "กรุณากรอกข้อมูลให้ครบ",
          html: `<ul class="text-left list-disc ml-5">${missing
            .map((m) => `<li>${m}</li>`)
            .join("")}</ul>`,
          confirmButtonColor: "#E13F57",
        });
        return;
      }

      if (items.length === 0) {
        Swal.fire("กรุณาเพิ่มสินค้า/แพ็กเกจก่อนบันทึก", "", "warning");
        return;
      }
      if (subLocations.length > 0 && items.length > 1) {
        Swal.fire("เมื่อมีจุดย่อย จะมีได้แค่บริการเดียว", "", "warning");
        return;
      }
      // ================================
      // 🔄 คำนวณสถานะใหม่
      // ================================
      let newStatus = header.Doc_Status;

      if (header.Doc_Status === 2 && !contractInfo.hasContract) {
        newStatus = 1;
      }

      if (contractInfo.hasContract && Number(contractInfo.contractStatus) === 5) {
        newStatus = 1;
      }

      if (isLocked) {
        newStatus = header.Doc_Status;
      }

      // ================================
      // 📦 เตรียมข้อมูล payload
      // ================================
      const payload = {
        header: {
          ...header,
          ...serviceDetail,
          IsVat: header.IsVat ? 1 : 0,
          IsVatIn: header.IsVatIn ? 1 : 0,
          Doc_Date: header.Doc_Date ? new Date(header.Doc_Date) : null,
          Doc_Status: newStatus,
          Created_By_Id: localStorage.getItem("emp_id"),
          Created_By_Name: localStorage.getItem("name"),
        },
        details: items.map((i, idx) => {
          const base = i.Item_Qty * i.Item_Price_Unit - (i.Item_Discount || 0);
          let vat = 0;
          let total = base;
          if (header.IsVat) {
            vat = base * 0.07;
            total = base + vat;
          } else if (header.IsVatIn) {
            vat = base - base / 1.07;
            total = base;
          }
          return {
            Line: idx + 1,
            Pack_Id: i.Pack_Id,
            Pack_Name: i.Pack_Name,
            Item_Qty: i.Item_Qty,
            Item_Unit: i.Item_Unit,
            Item_Price_Unit: i.Item_Price_Unit,
            Item_Discount: i.Item_Discount,
            Item_VAT: vat,
            Item_Total: total,
            Item_Img: i.Item_Img || null,
          };
        }),
        subLocations: subLocations.map((s) => {
          const base = s.Qty * s.Price_Unit - s.Discount;
          let subVat = 0;
          let subTotal = base;
          if (header.IsVat) {
            subVat = base * 0.07;
            subTotal = base + subVat;
          } else if (header.IsVatIn) {
            subVat = base - base / 1.07;
            subTotal = base;
          }
          return {
            Sub_Name: s.Sub_Name,
            Qty: s.Qty,
            Price_Unit: s.Price_Unit,
            Discount: s.Discount,
            Sub_VAT: subVat,
            Sub_Total: subTotal,
            Sub_Img_Url: s.Sub_Img_Url || null,
            Sub_Img: s.Sub_Img || null,
          };
        }),
      };

      // ================================
      // 💾 บันทึกเอกสาร
      // ================================
      const res = Doc_Id
        ? await api.put(`/update-quote/${Doc_Id}`, payload)
        : await api.post("/create-quote", payload);

      const newDocId = Doc_Id || res.data?.Doc_Id;

      // ================================
      // 📤 อัปโหลดไฟล์แนบ (ถ้ามี)
      // ================================
      const newFiles = selectedFiles.filter((f) => f.rawFile instanceof File);

      if (newFiles.length > 0 && newDocId) {
        const formData = new FormData();
        newFiles.forEach((file) => formData.append("files", file.rawFile));

        formData.append("Comp_Id", localStorage.getItem("company"));
        formData.append("Upload_By", localStorage.getItem("name"));
        formData.append("DocCode_ID", 6);
        formData.append("Doc_ID", newDocId);

        try {
          await api.post(`/upload-quote-file/${newDocId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uploadErr) {
          console.error("❌ Upload failed:", uploadErr);
          Swal.fire("เกิดข้อผิดพลาด", "อัปโหลดไฟล์ไม่สำเร็จ", "error");
        }
      }

      navigate(`/quotelist/${AR_Id ?? id}`);

    } catch (err) {
      console.error("Error save quote:", err);
      Swal.fire("เกิดข้อผิดพลาด", err.message, "error");
    }
  };


  const handleApprove = async () => {
    const confirm = await Swal.fire({
      title: "ยืนยันการอนุมัติ?",
      text: "คุณต้องการอนุมัติใบเสนอราคานี้หรือไม่?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "อนุมัติ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#28a745",
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.put(`/approve-quote/${Doc_Id}`);
      navigate(`/quotelist/${header.AR_Id ?? id}`);
    } catch (err) {
      Swal.fire("ผิดพลาด", err.message, "error");
    }
  };

  const handleReject = async () => {
    const { value: reason } = await Swal.fire({
      title: "กรุณากรอกเหตุผลที่ไม่อนุมัติ",
      input: "textarea",
      inputPlaceholder: "ระบุเหตุผล...",
      showCancelButton: true,
      confirmButtonText: "ดำเนินการต่อ",
      cancelButtonText: "ยกเลิก",
    });

    if (!reason) return;

    const confirm = await Swal.fire({
      title: "ยืนยันการไม่อนุมัติ?",
      text: "คุณต้องการไม่อนุมัติใบเสนอราคานี้หรือไม่?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ไม่อนุมัติ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.put(`/reject-quote/${Doc_Id}`, {
        Reject_Reason: reason,
        Update_By_Id: localStorage.getItem("emp_id"),
        Update_By_Name: localStorage.getItem("name"),
      });

      navigate(`/quotelist/${header.AR_Id ?? id}`);
    } catch (err) {
      Swal.fire("ผิดพลาด", err.message, "error");
    }
  };

  return (
    <div className="flex text-14 h-screen overflow-hidden">
      <Sidebar />
      <main
        className="flex-1 h-screen bg-gray-50 flex flex-col"
        style={{
          height: "calc(100vh - 56px)",
          overflowX: "hidden",
          overflowY: "hidden",
          marginLeft: "var(--sidebar-width)",
          marginTop: "56px",
        }}
      >
        <div className="flex-shrink-0 pl-2 pr-6 pt-0.5">
          <Topbar breadcrumbs={topbarBreadcrumbs} />
        </div>

        <div className="flex-1 min-h-0 overflow-auto pl-2 pr-6 pb-40 pt-0.5 space-y-6">
          <QuoteHeader
            header={header}
            setHeader={setHeader}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            subLocations={subLocations}
            setSubLocations={setSubLocations}
            itemsCount={items.length}
            serviceLocations={serviceLocations.filter(l =>
              (l.ServicePurpose_Name || "").trim() === "ที่อยู่บริการ" || Number(l.ServicePurpose) === 6
            )}
            contractLocations={serviceLocations.filter(l =>
              (l.ServicePurpose_Name || "").trim() === "ที่อยู่ทำสัญญา" || Number(l.ServicePurpose) === 7
            )}
            contractInfo={contractInfo}
            disabled={isLocked}
          />


          <QuoteItemsTable
            items={items}
            handleChange={(idx, field, value) => {
              if (isLocked) return;
              const newItems = [...items];
              newItems[idx][field] =
                ["Item_Qty", "Item_Price_Unit", "Item_Discount"].includes(field)
                  ? Number(value) || 0
                  : value;
              setItems(newItems);
            }}
            handleDelete={(idx) => {
              if (isLocked) return;
              setItems(items.filter((_, i) => i !== idx));
            }}
            header={header}
            setHeader={setHeader}
            onAddItem={() => !isLocked && setModalOpen(true)}
            disabled={isLocked}
            maxItems={subLocations.length > 0 ? 1 : undefined}
            hasSubLocations={subLocations.length > 0}
          />

          <ServiceDetails
            serviceDetail={serviceDetail}
            setServiceDetail={setServiceDetail}
            disabled={isLocked}
          />

          <QuoteDocumentInfo
            header={{
              ...header,
              Doc_Detail: header.Doc_Detail ?? "",
              Doc_Remark: header.Doc_Remark ?? "",
            }}
            setHeader={setHeader}
          />

          {/* ===== Sticky Footer ===== */}
          <div className="fixed bottom-0 left-[var(--sidebar-width)] right-0 bg-white shadow-lg border-t p-4 z-[50]">
            <QuoteActions
              isEdit={!!Doc_Id}
              docStatus={header.Doc_Status}
              isDataLoaded={isDataLoaded}
              onSave={() => handleSave()}
              onApprove={() => handleApprove()}
              onReject={() => handleReject()}
              disabled={isLocked}
            />
          </div>

        </div>
      </main>

      {modalOpen && !isLocked && (
        <PackageModal
          isOpen={modalOpen}
          selectedIds={items.map((i) => i.Pack_Id || i.Item_Id)}
          onClose={() => setModalOpen(false)}
          onSelect={(selected) => {
            // "บริการ" tab รวม packages + services ไว้ใน payload แยก key
            const selectedList =
              selected.type === "package"
                ? [...(selected.packages || []), ...(selected.services || [])]
                : selected.items || [];

            const newItems = selectedList.map((p) => {
              let previewImg = null;

              try {
                const bufferData = p.Item_Img?.data || p.Pack_Img?.data;
                if (Array.isArray(bufferData) && bufferData.length > 0) {
                  const blob = new Blob([Uint8Array.from(bufferData)], {
                    type: "image/jpeg",
                  });
                  previewImg = URL.createObjectURL(blob);
                }
              } catch (err) {
                console.error("❌ Error creating blob:", err);
              }

              return {
                Pack_Id: selected.type === "item" ? p.Item_Id
                       : p._src === "service" ? p.Service_Id
                       : p.Pack_Id,
                Pack_Name: selected.type === "item" ? p.Item_Name
                         : p._src === "service" ? p.Service_Name
                         : p.Pack_Name,
                Item_Qty: 1,
                Item_Unit: selected.type === "item" ? "สินค้า"
                         : p._src === "service" ? "บริการ"
                         : "แพ็กเกจ",
                Item_Price_Unit: p.Pack_Price || p.Item_Price || 0,
                Item_Discount: 0,
                Item_Img: p.Item_Img || p.Pack_Img || null,
                Item_Img_Url: previewImg,
              };
            });

            setItems((prev) => structuredClone([...prev, ...newItems]));
            setModalOpen(false);
          }}
        />
      )}

      {showFileModal && (
        <FileModalLocation
          mode="E"
          showViewAttachModal={showFileModal}
          setShowViewAttachModal={setShowFileModal}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          Doc_Id={Doc_Id}
          docCodeId={6}
          onAfterSave={() => {
          }}
        />
      )}
    </div>
  );
}
